/**
 * scene-host — the command seam (measurement plan §0) for driving a framed scene.
 *
 * Mirrors the theming command seam (`src/commands`): every host→guest verb is a
 * typed, UI-free dispatch that runs against an INJECTED transport (the frame wires
 * `iframe.contentWindow.postMessage`; tests/headless wire a recording fn), is
 * gated by the guest's declared capabilities, and emits EXACTLY ONE analytics
 * event per command driven (§0b — the single instrumentation point). No React,
 * no DOM here.
 */
import { track } from "@/lib/analytics";
import {
  command,
  supportsCommand,
  type SurfaceCommand,
  type SurfaceId,
  type SurfaceCapabilities,
  type SceneIdentity,
} from "./surface-protocol";

/** The v0 command vocabulary. Small and honest; extend as scenes grow. The guest
 *  declares which of these it handles in its `surface/hello`. */
export const SCENE_COMMAND = {
  /** Hand the guest its host context (brand, route, viewer). */
  SET_CONTEXT: "scene/setContext",
  /** Drive the scene to a creation beat (Beat 0 = arrival slate). */
  PRESENT: "scene/present",
  /** Liveness check. */
  PING: "scene/ping",
} as const;

export type SceneCommandMethod = (typeof SCENE_COMMAND)[keyof typeof SCENE_COMMAND];

// Typed params per verb — the "typed command envelope" the contract promises.
export interface SetContextParams {
  brandId: string | null;
  route: string;
  viewer?: string;
}
export interface PresentParams {
  beat: number;
}

/** Transport + id minting the seam needs. The frame supplies a `post` that calls
 *  `contentWindow.postMessage(env, targetOrigin)`; a recording fake supplies one
 *  that captures envelopes. */
export interface SceneHostDeps {
  post: (envelope: SurfaceCommand) => void;
}

/** What the host knows about the guest right now (from the handshake). */
export interface SceneContext {
  capabilities: SurfaceCapabilities | null;
  scene: SceneIdentity | null;
}

export type SendOutcome =
  | { sent: true; id: SurfaceId; envelope: SurfaceCommand }
  /** The guest never declared this verb — the host refuses to drive it. */
  | { sent: false; reason: "unsupported" };

/**
 * A stateful host bound to one framed guest. Holds the correlation-id counter so
 * every command gets a unique `id`. Create one per iframe mount.
 */
export function createSceneHost(deps: SceneHostDeps) {
  let seq = 0;

  /**
   * Drive one typed command at the guest. Capability-gated: if the guest didn't
   * declare `method`, nothing is posted and no event is emitted (the command was
   * not driven). On a real send, posts the envelope and emits ONE analytics event.
   * Never throws — a broken transport must not break the caller.
   */
  function send(ctx: SceneContext, method: SceneCommandMethod, params?: unknown): SendOutcome {
    if (!supportsCommand(ctx.capabilities, method)) {
      // eslint-disable-next-line no-console
      console.warn(`[scene] guest does not support ${method}; not driving it`);
      return { sent: false, reason: "unsupported" };
    }
    const id: SurfaceId = `cmd_${++seq}`;
    const envelope = command(id, method, params);
    try {
      deps.post(envelope);
    } catch {
      // Transport failure is not the caller's problem; swallow (frame may reload).
    }
    // The ONE event per command (§0b). Properties mirror the envelope; params are
    // intentionally NOT spread wholesale (they can carry free-form scene data) —
    // only the method + scene identity, matching the theming seam's discipline.
    track("Scene Command Sent", { method, sceneId: ctx.scene?.id ?? null });
    return { sent: true, id, envelope };
  }

  // Typed convenience wrappers — callers get param types, one event each.
  return {
    send,
    setContext(ctx: SceneContext, params: SetContextParams): SendOutcome {
      return send(ctx, SCENE_COMMAND.SET_CONTEXT, params);
    },
    present(ctx: SceneContext, params: PresentParams): SendOutcome {
      return send(ctx, SCENE_COMMAND.PRESENT, params);
    },
    ping(ctx: SceneContext): SendOutcome {
      return send(ctx, SCENE_COMMAND.PING);
    },
  };
}

export type SceneHost = ReturnType<typeof createSceneHost>;
